// hooks/useSegmentSettings.js
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  pushToHistoryAction,
  updateSegmentSetting,
} from "../redux/reducers/segmentBuilder";
import {
  getSelectedSegment,
  getWheelColorPalette,
} from "../redux/selectors/segmentBuilderSelectors";
import { solidColors } from "../constants/segmentTools";
import { convertKeysToSnakeCase } from "../utils/helpers";
import { convertGlobalSettingsToApiFormat } from "../utils/segmentBuilderUtils";
import { usePaletteColor } from "./usePaletteColor";
import { getActiveSubscriptionData } from "../redux/selectors/subscriptionSelectors";
import { serializeSegmentNodes } from "../utils/segmentPayloadHygiene";

export default function useSegmentSettings() {
  const selectedArc = useSelector(getSelectedSegment);
  const { data: currentPlan } = useSelector(getActiveSubscriptionData);
  const wheelColorPalette = useSelector(getWheelColorPalette) || {};
  const { getPaletteColor } = usePaletteColor();

  const dispatch = useDispatch();
  const handleChange = (type, sideOrValue, value) => {
    if (selectedArc && selectedArc.settings) {
      const updatedSettings = { ...selectedArc.settings };

      switch (type) {
        case "segmentBgColor":
          updatedSettings.segmentBgColor = getPaletteColor(value);
          updatedSettings.currentBg = 0;
          break;
        case "segmentBgGradientColor":
          updatedSettings.segmentBgColor = value;
          updatedSettings.currentBg = 3;
          break;
        case "wallpaperTransparency":
          updatedSettings.wallpaperTransparency = value;
          break;
        case "wallpaperResizing":
          updatedSettings.wallpaperResizing = value;
          break;
        case "addWallpaperOverlayColor":
          updatedSettings.wallpaperOverlayColor = getPaletteColor(value);
          updatedSettings.currentBg = 1;
          break;
        case "resetWallpaperOverlayColor":
          updatedSettings.wallpaperOverlayColor = "ffffff";
          break;
        case "addBgWallpaper":
          updatedSettings.bgWallpaper = getPaletteColor(value);
          updatedSettings.isPattern = sideOrValue;
          updatedSettings.currentBg = sideOrValue ? 1 : 2;
          break;
        case "addPatternId":
          updatedSettings.patternId = value;
          updatedSettings.isPattern = 1;
          updatedSettings.currentBg = 1;
          break;
        case "segmentTitle":
          updatedSettings.title = value || "";
          break;
        case "segmentColor":
          updatedSettings.color = getPaletteColor(value);
          break;
        case "segmentIcon":
          updatedSettings.icon = value;
          break;

        case "addPatternColor":
          updatedSettings.patternColor = getPaletteColor(value);
          updatedSettings.currentBg = 1;
          break;
        case "addPatternColorInitially":
          updatedSettings.patternColor = getPaletteColor(value);
          updatedSettings.currentBg = 0;
          break;
        case "patternColorType":
          updatedSettings.selectedColorType = value ?? 0;
          updatedSettings.currentBg = 1;
          break;
        case "addResizeMode":
          updatedSettings.resizeMode = value;
          break;
        case "wallpaperHuePercentage":
          updatedSettings.wallpaperHuePercentage = value;
          break;
        case "wallpaperHueColor":
          updatedSettings.wallpaperHueColor = value;
          break;

        default:
          break;
      }

      // fix-stg0919-segment-settings-edit-blows-up-builder-session: push a
      // snapshot of the CURRENT (pre-edit) state to a NEW history slot FIRST,
      // decoupling currentIndex from lastSavedIndex, THEN apply the edit.
      // Right before any edit, currentIndex === lastSavedIndex (the resting
      // "saved" state) — dispatching updateSegmentSetting() first mutated
      // state.history[state.currentIndex] IN PLACE, which at that instant IS
      // the exact same array slot lastSavedIndex points to, silently
      // rewriting the "last saved" snapshot to already contain the unsaved
      // edit. That corrupted baseline then made every dirty/reanchor check
      // downstream (contentDirty.js, hydrationBaseline.js) see "nothing
      // changed", so the edit never triggered a save and was lost on reload.
      dispatch(pushToHistoryAction()); // Fork a new history entry first
      dispatch(
        updateSegmentSetting({
          id: selectedArc.id,
          data: updatedSettings,
        })
      ); // Mutate only the NEW (now-current) entry — lastSavedIndex's entry is untouched
    }
  };

  const lightenColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16),
      amt = Math.round(2.55 * percent),
      R = (num >> 16) + amt,
      G = ((num >> 8) & 0x00ff) + amt,
      B = (num & 0x0000ff) + amt;
    return `#${(
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
      .toUpperCase()}`;
  };

  const getColor = (wheelData, index, palette) => {
    const excludedColors = [
      "#FFFFFF",
      "#E9ECEF",
      "#DEE2E6",
      "#CED4DA",
      "#ADB5BD",
      "#868E96",
      "#495057",
      "#FFC9C9",
      "#EEBEFA",
      "#D0BFFF",
      "#BAC8FF",
      "#A5D8FF",
      "#96F2D7",
      "#B2F2BB",
      "#D8F5A2",
      "#FFEC99",
      "#FFD8A8",
      "#343A40",
      "#212529",
    ];
    const arrayLength = wheelData?.length;
    const selectedWheelPalette = palette || wheelColorPalette;

    if (selectedWheelPalette?.colors?.length && currentPlan?.sub_tier !== 0) {
      const segmentIndex =
        index >= selectedWheelPalette.colors.length
          ? index % selectedWheelPalette.colors.length
          : index;

      const color = selectedWheelPalette.colors[segmentIndex]?.hex;
      const colorInner = color ? lightenColor(color, 20) : null;

      return { color, colorInner };
    } else {
      const lastAddedColor = arrayLength
        ? wheelData[arrayLength - 1]?.settings?.color
        : null;
      const firstAddedColor = arrayLength
        ? wheelData[0]?.settings?.color
        : null;

      const possibleColors = solidColors.filter(
        (color) =>
          !excludedColors.includes(color) &&
          color !== lastAddedColor &&
          color !== firstAddedColor
      );

      const randomIndex = Math.floor(Math.random() * possibleColors.length);
      const color = possibleColors[randomIndex];
      const colorInner = lightenColor(color, 20); // Lighten the color by 20%

      return { color, colorInner };
    }
  };

  const getMaxPosition = (wheelData = []) => {
    const sortedChart = wheelData?.sort((a, b) => a.position - b.position);
    return sortedChart[sortedChart.length - 1]?.position ?? 0;
  };

  const handleRotationData = (wheelData = [], direction = "prev") => {
    let chartdata = [...wheelData].map((item) => ({ ...item }));
    if (direction === "prev") {
      const firstElement = chartdata.shift();
      if (firstElement) {
        chartdata.push(firstElement);
      }
    } else if (direction === "next") {
      const lastElement = chartdata.pop();
      if (lastElement) {
        chartdata.unshift(lastElement);
      }
    }
    chartdata = chartdata.map((d, index) => ({ ...d, position: index * 10 }));
    return chartdata;
  };

  // stg-0920-save-fix-5891-ineffective: `generatePayload` reads no
  // hook/component state — it is a PURE function of its own arguments (same
  // contract dev's `generatePayload = generateWheelPayload` module-level
  // alias already relies on) — so it must be REFERENTIALLY STABLE across
  // renders, via `useCallback` with an empty dependency array.
  //
  // Without this, a fresh arrow function was built on every
  // `useSegmentSettings()` call (i.e. every render of pages/SegmentBuilder/
  // index.jsx) — the THIRD occurrence of a bug class this codebase has
  // already hit and fixed twice: index.jsx's `savedPayloadBaselineRef`
  // re-derivation effect lists `generatePayload` in its dependency array (see
  // saveIndicatorEditorStateSkew.test.js / selectedArcBaselineSkew.test.js for
  // the first two — the reactive `editorState` variable, then the
  // `selectedArc` OBJECT, were the unstable deps that poisoned this same
  // effect before). An unstable `generatePayload` re-runs that effect on
  // EVERY render (not just when a save lands or the segment switches), and
  // each re-run re-snapshots `savedPayloadBaselineRef.current` from
  // `editorStateRef.current` — the LIVE, current canvas — so the baseline is
  // continuously re-poisoned to match "current" before `isContentDirty`
  // (contentDirty.js) can ever see a difference. MEASURED (acceptance proof,
  // main.656e852d.js, web-stg.iyuga.net): add/delete/move/reorder a block and
  // remove a lock ALL sat unpersisted past 90-110s — every one of those gates
  // on this same `isContentDirtyValue`, so one unstable reference silently
  // disabled the entire structural-autosave family plus the 30s content
  // fallback. See useSegmentSetting.generatePayloadIdentity.stgport0920.
  // test.js for the prove-can-fail reproduction.
  const generatePayload = useCallback((
    wheel_information = {},
    wheelWallpaperSettings = {}
  ) => {
    let payload = {
      type: 2,
      take_screenshot: wheel_information.take_screenshot ?? false,
      wheel_preview: wheel_information.wheel_preview ?? "",
      version: wheel_information.version || 2,
      business_box: wheel_information.business_box?.business_title
        ? wheel_information.business_box
        : null,
      is_primary: wheel_information.is_primary ?? false,
      visibility: wheel_information.visibility ?? 1,
      palette_id: wheel_information.palette_id,

      is_locked: wheel_information.is_locked ?? false,
      show_business_box: wheel_information.show_business_box ?? true,

      wheel_name:
        wheel_information.wheel_name === "Wheel Name placeholder"
          ? ""
          : wheel_information.wheel_name,
      wallpaper_settings: {
        wallpaper_transparency:
          wheelWallpaperSettings.wallpaperTransparency ?? 50,
        wallpaper_resizing: wheelWallpaperSettings.wallpaperResizing ?? 20,
        wallpaper_overlay_color:
          wheelWallpaperSettings.wallpaperOverlayColor ?? "#ffffff",
        bg_wallpaper: wheelWallpaperSettings.bgWallpaper ?? "",
        is_pattern: wheelWallpaperSettings.isPattern ?? false,
        segment_bg_color: wheelWallpaperSettings.segmentBgColor ?? "#ffffff",
        current_bg: wheelWallpaperSettings.currentBg ?? 0,
        selected_color_type: wheelWallpaperSettings.selectedColorType ?? 1,
        pattern_color: wheelWallpaperSettings.patternColor ?? "#000000",
        wallpaper_hue_percentage: wheelWallpaperSettings.wallpaperHuePercentage ?? 0,
        wallpaper_hue_color: wheelWallpaperSettings.wallpaperHueColor ?? "#ffffff",
      },
      wheel_center_media: {
        type: wheel_information?.wheel_center_media?.type || "image",
        url: wheel_information?.wheel_center_media?.url || "",
      },
      show_segment_icon: wheel_information?.segment_icon ?? "blank", // Note the typo fix here from "segment_icon" to "show_segment_icon"
      description: wheel_information?.description ?? "",
      sub_title:
        wheel_information?.sub_title === "Subtitle"
          ? ""
          : wheel_information?.sub_title,
      // ISP-9999 (stgport-education-category-discover-wheel-tags): categories
      // + wheel_tags were never part of this payload -- staging's Wheel
      // Settings panel had no Categories/Tags field at all.
      categories: wheel_information?.categories ?? [],
      wheel_tags: (wheel_information?.wheel_tags ?? []).map((tag) =>
        typeof tag === "string" ? { name: tag } : tag
      ),
      wheel_segments: (() => {
        if (!wheel_information || !wheel_information?.wheel_segments) return [];

        const segmentIds = new Set();
        return wheel_information?.wheel_segments.map((segment) => {
          let currentId = segment.id;
          // Ensure ID is a number for incrementing
          if (typeof currentId !== "number") {
            // If it's not a number, convert to a number or generate a new one if it's not convertible
            const parsedId = parseInt(currentId, 10);
            currentId = isNaN(parsedId) ? Date.now() : parsedId;
          }

          while (segmentIds.has(currentId)) {
            currentId += 1;
          }
          segmentIds.add(currentId);

          const { initialNode, ...restSegment } = segment;
          const segmentSettingsSnake = convertKeysToSnakeCase(segment.settings || {});
          return {
            ...restSegment,
            id: currentId,
            text_body: "",
            pattern_id: segment?.patternId ?? 0,
            resizeMode: segment?.resizeMode ?? "1",
            // kelly-0921-round-5 (ports dev PR #2196, commit 0d3da66e): a blind
            // JSON.stringify double-encodes `segment.nodes` once it is already a
            // JSON string (craft's serialize() output, folded in for the
            // currently- or just-switched-away-from segment) -- MEASURED live on
            // web-stg.iyuga.net as the exact shape of the destroying late write.
            // serializeSegmentNodes (already on this branch, byte-identical to
            // dev) encodes exactly once regardless of whether `nodes` arrived as
            // an object or a string.
            nodes: serializeSegmentNodes(segment.nodes),
            settings: {
              ...segmentSettingsSnake,
              wallpaper_hue_percentage: segment?.settings?.wallpaperHuePercentage ?? 0,
              wallpaper_hue_color: segment?.settings?.wallpaperHueColor ?? "#ffffff",
            },
          };
        });
      })(),
      background: wheel_information?.background || {
        type: "",
        value: "",
      },
      image_links: wheel_information?.image_links || [],
      video_links: wheel_information?.video_links || [],
      document_links: wheel_information?.document_links || [],
      audio_links: wheel_information?.audio_links || [],
      starred_links: wheel_information?.starred_links || [],
      lock_type: {
        description: "test lock type",
        access_code_lock: {
          is_active: wheel_information?.lock_type?.access_code_lock?.is_active,
          access_codes:
            wheel_information?.lock_type?.access_code_lock?.access_codes || [],
        },
        permission_lock: {
          is_active: wheel_information?.lock_type?.permission_lock?.is_active,
          // members: wheel_information?.lock_type?.permission_lock?.members || [],
          emails: wheel_information?.lock_type?.permission_lock?.emails || [],
        },
      },
      is_clonable: wheel_information?.is_clonable,
      is_live: wheel_information?.is_live,
      is_draft: wheel_information?.is_draft,
      global_settings: convertGlobalSettingsToApiFormat(
        wheel_information?.globalSettings
      ),
      automatic_resizing: wheel_information?.automatic_resizing ?? false,
    };
    return payload;
  }, []);
  return {
    segmentSettings: selectedArc?.settings || {},
    handleChange,
    getMaxPosition,
    handleRotationData,
    generatePayload,
    getColor,
  };
}
